import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionFrame } from "../atoms/SectionFrame";
import { GlassCard } from "../atoms/GlassCard";
import { PrimaryButton, GhostButton } from "../atoms/Buttons";
import { FadeIn } from "../atoms/FadeIn";
import { TID } from "../../lib/testIds";
import { CALENDLY_URL } from "../../lib/links";
import { useContactForm, CONTACT_STEPS } from "../../hooks/useContactForm";

const EASE = [0.25, 0.1, 0.25, 1];

export function ContactSection() {
    const form = useContactForm();

    return (
        <SectionFrame innerClassName="" data-testid={TID.contactSection}>
            <div className="py-20 md:py-32 px-6 md:px-12">
                <FadeIn y={40} className="mb-12 md:mb-16">
                    <div className="text-base uppercase tracking-[0.3em] mb-4" style={{ color: "var(--accent)" }}>
                        CONTACT
                    </div>
                    <h3
                        className="display-text font-black uppercase"
                        style={{ fontSize: "clamp(2rem, 6vw, 4.5rem)", lineHeight: 1, letterSpacing: "-0.03em" }}
                    >
                        Tell us what<br />needs moving.
                    </h3>
                </FadeIn>

                <FadeIn y={40} delay={0.15}>
                    <GlassCard elevated rounded="rounded-[36px]" className="p-6 md:p-12 max-w-[820px] mx-auto">
                        <ProgressLine progress={form.progress} />
                        {!form.done ? <FormSteps form={form} /> : <CalendlySuccess data={form.data} />}
                    </GlassCard>
                </FadeIn>
            </div>
        </SectionFrame>
    );
}

function ProgressLine({ progress }) {
    return (
        <div className="relative h-[2px] mb-10" style={{ background: "var(--line)" }}>
            <motion.div
                className="absolute inset-0 origin-left"
                style={{
                    background: "linear-gradient(90deg, var(--accent), var(--accent))",
                    boxShadow: "0 0 12px var(--accent-soft), 0 0 32px var(--accent-soft)",
                }}
                animate={{ scaleX: progress }}
                transition={{ duration: 0.6, ease: EASE }}
            />
        </div>
    );
}

function FormSteps({ form }) {
    const { step, cur } = form;
    return (
        <div className="min-h-[300px] flex flex-col">
            <div
                className="flex items-center justify-between text-sm uppercase tracking-[0.25em] mb-6"
                style={{ color: "var(--muted)" }}
            >
                <span>Step {step + 1} / {CONTACT_STEPS.length}</span>
                <span>{cur.key.toUpperCase()}</span>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={cur.key}
                    initial={{ opacity: 0, y: 30, filter: "blur(6px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -30, filter: "blur(6px)" }}
                    transition={{ duration: 0.5, ease: EASE }}
                    data-testid={TID.contactStep(step)}
                    className="flex-1"
                >
                    <label
                        className="block display-text font-medium uppercase mb-6"
                        style={{ fontSize: "clamp(1.4rem, 4vw, 2.4rem)", lineHeight: 1.1, letterSpacing: "-0.02em" }}
                    >
                        {cur.label}
                    </label>
                    <StepField form={form} />
                </motion.div>
            </AnimatePresence>

            <StepControls form={form} />
        </div>
    );
}

function StepField({ form }) {
    const { step, cur, data, update } = form;

    if (cur.type === "textarea") {
        return (
            <textarea
                data-testid={TID.contactInput(step)}
                value={data[cur.key]}
                onChange={(e) => update(e.target.value)}
                placeholder={cur.placeholder}
                rows={4}
                className="w-full bg-transparent border rounded-[20px] px-5 py-4 text-base md:text-lg outline-none focus:border-[var(--accent)] transition-colors resize-none"
                style={{ color: "var(--text)", borderColor: "var(--line)" }}
            />
        );
    }

    if (cur.type === "chips") {
        return (
            <div className="flex flex-wrap gap-3">
                {cur.options.map((opt) => {
                    const active = data[cur.key] === opt;
                    return (
                        <button
                            key={opt}
                            type="button"
                            data-testid={`${TID.contactInput(step)}-${opt.toLowerCase()}`}
                            onClick={() => update(opt)}
                            className="rounded-full border-2 px-6 py-3 text-base uppercase tracking-[0.2em] transition-all"
                            style={{
                                color: active ? "var(--bg)" : "var(--text)",
                                background: active ? "var(--accent)" : "transparent",
                                borderColor: active ? "var(--accent)" : "var(--line)",
                                boxShadow: active ? "0 0 24px var(--accent-soft)" : "none",
                            }}
                        >
                            {opt}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <input
            data-testid={TID.contactInput(step)}
            type={cur.inputType || "text"}
            value={data[cur.key]}
            onChange={(e) => update(e.target.value)}
            placeholder={cur.placeholder}
            className="w-full bg-transparent border rounded-full px-6 py-4 text-base md:text-lg outline-none focus:border-[var(--accent)] transition-colors"
            style={{ color: "var(--text)", borderColor: "var(--line)" }}
        />
    );
}

function StepControls({ form }) {
    const { step, isLastStep, submitting, next, back, submit } = form;
    return (
        <div className="mt-10 flex items-center justify-between gap-4">
            <GhostButton
                label="Back"
                onClick={back}
                data-testid={TID.contactBack}
                className={step === 0 ? "opacity-30 pointer-events-none" : ""}
            />
            {!isLastStep ? (
                <PrimaryButton label="Next" onClick={next} data-testid={TID.contactNext} />
            ) : (
                <PrimaryButton
                    label={submitting ? "Sending..." : "Send it over"}
                    onClick={submit}
                    data-testid={TID.contactSubmit}
                />
            )}
        </div>
    );
}

function CalendlySuccess({ data }) {
    const src =
        `${CALENDLY_URL}?embed_domain=${window.location.hostname}&embed_type=Inline&hide_gdpr_banner=1` +
        `&background_color=0c0c0c&text_color=ececec&primary_color=d8ff3d` +
        `&name=${encodeURIComponent(data.name)}&email=${encodeURIComponent(data.email)}`;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            data-testid={TID.contactSuccess}
            className="text-center py-6"
        >
            <h4
                className="display-text font-black uppercase mb-4"
                style={{ fontSize: "clamp(1.6rem, 4vw, 2.6rem)", letterSpacing: "-0.02em" }}
            >
                Got it. Now pick a time.
            </h4>
            <p style={{ color: "var(--muted)" }} className="text-base mb-8">
                Your message is on its way — lock in your demo below.
            </p>
            <div className="overflow-hidden rounded-[24px] border" style={{ borderColor: "var(--line)", background: "#0C0C0C" }}>
                <iframe
                    data-testid={TID.calendlyEmbed}
                    title="Book a demo"
                    src={src}
                    className="w-full"
                    style={{ height: "680px", border: "none" }}
                />
            </div>
        </motion.div>
    );
}
